import { useMemo, useCallback, useState, useEffect, createContext, useContext } from 'react';
import { useMyAccount } from '@ux/accounts-client';
import cookies from 'browser-cookies';
import { useBrandConfig } from '@ux/whitelabel';
import { jsx } from 'react/jsx-runtime';

const useSupportedCurrencies = () => {
  var _config$payment2;
  const config = useBrandConfig();
  // We need to memoize this value since it us used in an effect.
  // if payment.currencies is undefined, it will return a new array on each render
  // Effectively causing an infinite loop.
  // We should consider returning null or undefined instead
  const currencies = useMemo(() => {
    var _config$payment$curre, _config$payment;
    return (_config$payment$curre = config === null || config === void 0 ? void 0 : (_config$payment = config.payment) === null || _config$payment === void 0 ? void 0 : _config$payment.currencies) != null ? _config$payment$curre : [];
  }, [config === null || config === void 0 ? void 0 : (_config$payment2 = config.payment) === null || _config$payment2 === void 0 ? void 0 : _config$payment2.currencies]);
  return currencies;
};

// in general we want to set the cookie on the brand subdomain so we can share
// cookies between apps (usf-uop), however we need the COOKIE_DOMAIN override
// so we can get this to work on localhost
const useBrandDomain = () => {
  var _config$domain;
  const config = useBrandConfig();
  return (_config$domain = config === null || config === void 0 ? void 0 : config.domain) != null ? _config$domain : '';
};
const getEnvDomain = () => {
  try {
    return COOKIE_DOMAIN;
  } catch (e) {
    return null;
  }
};
const useDomain = () => {
  const brandDomain = useBrandDomain();
  const envDomain = getEnvDomain();
  return envDomain != null ? envDomain : brandDomain;
};

const getPreferredCurrency = cookies => () => {
  var _cookies$get;
  return (_cookies$get = cookies.get('ux-currency')) != null ? _cookies$get : '';
};
const savePreferredCurrency = cookies => (value, domain) => {
  if (value) {
    cookies.set('ux-currency', value, {
      domain
    });
  }
};

function calculateAndSaveCurrency(_ref) {
  let {
    getCurrency = getPreferredCurrency(cookies),
    accountCurrency,
    supportedCurrencies,
    saveCurrency = savePreferredCurrency(cookies),
    domain
  } = _ref;
  const preferred = getCurrency();
  let result = preferred;
  if (accountCurrency != null && accountCurrency !== '') {
    result = accountCurrency;
  }
  if (supportedCurrencies.indexOf(result) < 0) {
    result = supportedCurrencies[0];
  }
  if (result != null && result !== '' && result !== preferred && domain != null) {
    saveCurrency(result, domain);
  }
  return result;
}

const useCalculateCurrency = _ref => {
  let {
    getCurrency,
    saveCurrency,
    useUseSupportedCurrencies,
    calculateAndSaveCurrency
  } = _ref;
  const {
    data: account
  } = useMyAccount();
  const currencies = useUseSupportedCurrencies();
  const domain = useDomain();
  const fn = useCallback(() => {
    var _account$currency;
    return calculateAndSaveCurrency({
      getCurrency,
      accountCurrency: (_account$currency = account === null || account === void 0 ? void 0 : account.currency) != null ? _account$currency : '',
      saveCurrency,
      domain,
      supportedCurrencies: currencies
    });
  }, [account === null || account === void 0 ? void 0 : account.currency, calculateAndSaveCurrency, currencies, domain, getCurrency, saveCurrency]);
  return fn;
};
function useUseCalculateCurrency() {
  const saveCurrency = useMemo(() => {
    return savePreferredCurrency(cookies);
  }, []);
  const getCurrency = useMemo(() => {
    return getPreferredCurrency(cookies);
  }, []);
  return useCalculateCurrency({
    saveCurrency,
    getCurrency,
    useUseSupportedCurrencies: useSupportedCurrencies,
    calculateAndSaveCurrency
  });
}

const CurrencyContext = /*#__PURE__*/createContext(undefined);
const CurrencyActionContext = /*#__PURE__*/createContext(undefined);
const CurrencyProvider = _ref => {
  let {
    children
  } = _ref;
  const {
    data: account
  } = useMyAccount();
  const accountCurrency = account === null || account === void 0 ? void 0 : account.currency;
  const domain = useDomain();
  const supportedCurrencies = useSupportedCurrencies();
  const [state, setState] = useState({
    currency: ''
  });
  useEffect(() => {
    setState({
      currency: calculateAndSaveCurrency({
        accountCurrency: accountCurrency != null ? accountCurrency : supportedCurrencies[0],
        domain,
        supportedCurrencies
      })
    });
  }, [accountCurrency, domain, supportedCurrencies]);
  const setCurrency = useCallback(currency => {
    setState({
      currency
    });
    savePreferredCurrency(cookies)(currency, domain);
  }, [domain]);
  return /*#__PURE__*/jsx(CurrencyContext.Provider, {
    value: state,
    children: /*#__PURE__*/jsx(CurrencyActionContext.Provider, {
      value: setCurrency,
      children: children
    })
  });
};
const useCurrencyContext = () => {
  const context = useContext(CurrencyContext);
  if (context == null) {
    throw new Error('useCurrencyContext must be inside inside a CurrencyProvider');
  }
  return context;
};
const useCurrencyActionContext = () => {
  const context = useContext(CurrencyActionContext);
  if (context == null) {
    throw new Error('useCurrencyActionContext must be inside inside a CurrencyProvider');
  }
  return context;
};

const useSaveCurrency = () => {
  const saveCurrency = useCurrencyActionContext();
  return saveCurrency;
};
var useSaveCurrency$1 = useSaveCurrency;

const useCurrency = () => {
  const {
    currency
  } = useCurrencyContext();
  return currency;
};
var useCurrency$1 = useCurrency;

const TestProvider = _ref => {
  let {
    children,
    currency = 'GBP'
  } = _ref;
  const [state, setState] = useState({
    currency
  });
  const setCurrency = useCallback(currency => {
    setState({
      currency
    });
  }, []);
  return /*#__PURE__*/jsx(CurrencyContext.Provider, {
    value: state,
    children: /*#__PURE__*/jsx(CurrencyActionContext.Provider, {
      value: setCurrency,
      children: children
    })
  });
};
var TestProvider$1 = TestProvider;

export { CurrencyProvider, TestProvider$1 as TestProvider, useUseCalculateCurrency as useCalculateCurrency, useCurrency$1 as useCurrency, useSaveCurrency$1 as useSaveCurrency, useSupportedCurrencies };
